/**
 * AddCreditsDialog — OA credits top-up request (iter-2026 Phase 2).
 * Custom amount input + quick-select pills. On submit, posts to
 * /billing/credits/request-topup which emails the Analyzio team.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Plus } from "lucide-react";
import { toast } from "sonner";

const QUICK_AMOUNTS = [500, 1000, 2500, 5000];

export function AddCreditsDialog({ open, onOpenChange }) {
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reset = () => { setAmount(""); setNote(""); };

    const submit = async () => {
        const amt = Number(amount);
        if (!amt || amt <= 0) { toast.error("Enter a valid credit amount"); return; }
        setSubmitting(true);
        try {
            const r = await api.post("/billing/credits/request-topup", { amount: amt, note });
            const auto = Number(r?.data?.auto_approved || 0);
            const pending = Number(r?.data?.pending || 0);
            if (auto > 0 && pending > 0) {
                toast.success(`${auto.toLocaleString()} AI credits were automatically approved and added. The remaining ${pending.toLocaleString()} credits have been sent for approval.`);
            } else if (auto > 0) {
                toast.success(`${auto.toLocaleString()} AI credits were automatically approved and added to your account.`);
            } else {
                toast.success("Top-up request sent for approval. Our team will reach out shortly.");
            }
            // AI resumes immediately — clear any "credits used up" nudge and tell
            // every credit-balance widget to refetch so the UI reflects the new
            // allowance without a reload.
            if (auto > 0) {
                try { toast.dismiss("ai-credits-exhausted"); } catch { /* ignore */ }
                window.dispatchEvent(new CustomEvent("billing:credits-updated", {
                    detail: { auto, pending, new_allowance: r?.data?.new_allowance },
                }));
            }
            reset();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send request");
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!submitting) { if (!v) reset(); onOpenChange(v); } }}>
            <DialogContent data-testid="add-credits-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" /> Add AI credits
                    </DialogTitle>
                    <DialogDescription>
                        Tell us how many credits you need. Up to 200 are approved and added instantly; anything above that is sent to our team for approval.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-1">
                    <div>
                        <Label className="text-xs mb-1.5 block">Quick select</Label>
                        <div className="flex flex-wrap gap-2" data-testid="add-credits-pills">
                            {QUICK_AMOUNTS.map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => setAmount(String(q))}
                                    data-testid={`add-credits-pill-${q}`}
                                    className={`px-3 py-1.5 rounded-full text-sm font-mono border transition-colors ${
                                        String(q) === amount
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-card border-border hover:border-primary/50"
                                    }`}
                                >
                                    {q.toLocaleString()}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="credit-amount" className="text-xs mb-1.5 block">Credit amount</Label>
                        <Input
                            id="credit-amount"
                            type="number"
                            min="1"
                            placeholder="e.g. 1000"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            data-testid="add-credits-amount-input"
                        />
                    </div>
                    <div>
                        <Label htmlFor="credit-note" className="text-xs mb-1.5 block">Note (optional)</Label>
                        <Textarea
                            id="credit-note"
                            placeholder="Anything we should know about your usage?"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={3}
                            data-testid="add-credits-note-input"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="add-credits-cancel">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={submitting} data-testid="add-credits-submit">
                        <Plus className="h-4 w-4 mr-1" />
                        {submitting ? "Sending…" : "Send request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AddCreditsDialog;
